/**
 * Extract headings directly from web pages without external scripts
 * This utility function can be used by anyone who integrates the AdMesh UI SDK
 */

export interface HeadingsExtractionResult {
  title: string;
  headings: string[];
  article_text?: string;
  url: string;
  domain: string;
  extracted_at: string;
}

function titleFromPath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const raw = segments[segments.length - 1] || segments[segments.length - 2] || '';
  const withoutExtension = raw.replace(/\.[a-z0-9]+$/i, '');
  const cleaned = withoutExtension.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned
    ? cleaned.replace(/\b\w/g, (char) => char.toUpperCase())
    : 'This page';
}

/**
 * Extract headings directly from the current web page
 * Bypasses CSP restrictions by using direct DOM access
 * 
 * @returns {HeadingsExtractionResult | null} Object containing title, headings, and metadata
 */
export function extractHeadingsDirectly(): HeadingsExtractionResult | null {
  try {
    console.log('[AdMesh] Starting direct headings extraction...');
    
    // Clean and normalize text content
    const cleanText = (text: string) => text.replace(/\s+/g, " ").trim();

    const getArticleText = () => {
      const selectors = [
        'article',
        'main article',
        'main',
        '[role="main"]',
        '[data-admesh-article]',
        '.article-content',
        '.post-content',
        '.entry-content',
        '.content',
      ];

      const ignoredSelector = [
        'script',
        'style',
        'noscript',
        'svg',
        'canvas',
        'iframe',
        'nav',
        'header',
        'footer',
        'aside',
        '[aria-hidden="true"]',
        '[data-admesh-ignore]',
        '.admesh-publisher-assistant-root',
        '.admesh-publisher-assistant-reactMount',
        '.admesh-publisher-assistant-floating',
        '.admesh-publisher-assistant-inline',
      ].join(',');

      const candidates = selectors
        .map((selector) => document.querySelector(selector))
        .filter((element): element is Element => Boolean(element));

      const sourceElement = candidates
        .map((element) => {
          const clone = element.cloneNode(true) as Element;
          clone.querySelectorAll(ignoredSelector).forEach((node) => node.remove());
          const text = cleanText(clone.textContent || '');
          return { element, text };
        })
        .filter((candidate) => candidate.text.length >= 200)
        .sort((a, b) => b.text.length - a.text.length)[0]?.element || document.body;

      const clone = sourceElement.cloneNode(true) as Element;
      clone.querySelectorAll(ignoredSelector).forEach((node) => node.remove());

      const paragraphs = Array.from(clone.querySelectorAll('h1, h2, h3, p, li, blockquote'))
        .map((element) => cleanText(element.textContent || ''))
        .filter((text) => text.length >= 24);

      const articleText = paragraphs.length > 0
        ? paragraphs.join('\n')
        : cleanText(clone.textContent || '');

      return articleText.slice(0, 6000);
    };
    
    const fallbackTitle = titleFromPath(window.location.pathname || '/');
    
    // Extract title
    const title = cleanText(document.title || '') || fallbackTitle;
    
    // Extract all headings by selector and combine into single array
    const getAllHeadings = () => {
      const allHeadings: string[] = [];
      
      // Get all heading elements (h1 through h6)
      const headingElements = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
      
      // Convert NodeList to array and extract text
      Array.from(headingElements).forEach(element => {
        const text = cleanText(element.textContent || '');
        if (text.length > 0) {
          allHeadings.push(text);
        }
      });
      
      return allHeadings;
    };
    
    // Extract all headings in desired format
    const headings = getAllHeadings();
    const resolvedHeadings = headings.length > 0 ? headings : [title || fallbackTitle];
    
    // Create the simplified output format
    const result: HeadingsExtractionResult = {
      title: title,
      headings: resolvedHeadings,
      article_text: getArticleText(),
      url: window.location.href,
      domain: window.location.hostname,
      extracted_at: new Date().toISOString()
    };
    
    console.log('[AdMesh] Extracted headings:', {
      title: result.title,
      headingsCount: result.headings.length,
      articleTextLength: result.article_text?.length || 0,
      domain: result.domain
    });
    
    return result;
  } catch (error) {
    console.error('[AdMesh] Error in direct headings extraction:', error);
    return null;
  }
}
